import { useBreakpointMinLg } from '@kk/shared/hooks/useBreakpoint';
import { cn } from 'cn';
import { Link } from 'react-router-dom';
import { useTheme } from '#ui/hooks/useTheme.js';
import Icon, { type IconProps, type IconType } from '../Icon';
import Spinner from '../Spinner';
import { type ButtonVariantProps, buttonVariants } from './styles';

type Theme = ButtonVariantProps['theme'];

interface CommonLinkButtonProps {
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  icon?: IconType;
  iconProps?: IconProps;
  rightAligned?: boolean;
  compact?: boolean;
  inverted?: boolean;
  className?: string;
  preload?: number;
  subtle?: boolean;
  theme?: Theme;
  children?: React.ReactNode;
}

type HrefProps = {
  href: string;
  target?: React.HTMLAttributeAnchorTarget;
  to?: never;
};

type ToProps = {
  to: string;
  href?: never;
  target?: never;
};

type NoProps = {
  to?: never;
  target?: never;
  href?: never;
};

export type LinkButtonProps = CommonLinkButtonProps &
  (HrefProps | ToProps | NoProps);

const defaultLinkClasses = cn(
  'flex cursor-pointer items-center rounded border-none w-content',
  'hover:text-blue-40  hover:underline active:text-blue-110',
);

interface LinkButtonClassesProps {
  className: string | undefined;
  compact: boolean | undefined;
  icon: IconType | undefined;
  inverted: boolean | undefined;
  label: string | undefined;
  loading: boolean | undefined;
  rightAligned: boolean | undefined;
  subtle: boolean | undefined;
  theme: Theme | undefined;
}

function getLinkButtonClasses({
  className,
  compact,
  icon,
  inverted,
  label,
  loading,
  rightAligned,
  subtle,
  theme,
}: LinkButtonClassesProps): string {
  if (theme) {
    return cn(
      buttonVariants({
        theme,
        compact,
        inverted,
        rightAligned,
        loading,
        hasLabel: !!label,
        hasIcon: !!icon,
      }),
      className,
    );
  }

  return cn(
    defaultLinkClasses,
    subtle
      ? 'text-neutral-110 active:text-blue-110 active:no-underline'
      : 'font-medium text-blue-60 ',
    'disabled:text-neutral-60 aria-disabled:text-neutral-60',
    compact
      ? [
          'typ-link-xs',
          rightAligned
            ? { 'pr-1 pl-2': icon && !loading }
            : { 'pr-2 pl-1': icon && !loading },
        ]
      : [
          'typ-link',
          rightAligned
            ? { 'pr-2 pl-4': icon && !loading }
            : { 'pr-4 pl-2': icon && !loading },
        ],
    rightAligned && 'flex-row-reverse',
    className,
  );
}

interface LinkButtonContentProps {
  children: React.ReactNode;
  compact: boolean | undefined;
  icon: IconType | undefined;
  iconProps: IconProps | undefined;
  label: string | undefined;
  loading: boolean | undefined;
  rightAligned: boolean | undefined;
  subtle: boolean | undefined;
}

interface LinkButtonIconProps {
  compact: boolean | undefined;
  icon: IconType;
  iconProps: IconProps | undefined;
  rightAligned: boolean | undefined;
  subtle: boolean | undefined;
}

const iconMargins = {
  compact: { left: 'mr-2', right: 'ml-2' },
  default: {
    left: 'laane:mr-4 kunde:mr-2',
    right: 'laane:ml-4 kunde:mr-2',
  },
};

function LinkButtonIcon({
  compact,
  icon,
  iconProps,
  rightAligned,
  subtle,
}: LinkButtonIconProps) {
  const size = compact ? 'compact' : 'default';
  const alignment = rightAligned ? 'right' : 'left';

  return (
    <Icon
      size={compact || subtle ? 'tiny' : 'small'}
      {...(iconProps ? iconProps : {})}
      type={icon}
      className={cn(
        'flex-none self-center',
        iconMargins[size][alignment],
        iconProps?.className,
      )}
    />
  );
}

function LinkButtonContent({
  children,
  compact,
  icon,
  iconProps,
  label,
  loading,
  rightAligned,
  subtle,
}: LinkButtonContentProps) {
  if (loading) {
    return (
      <Spinner
        className={cn(
          'flex items-center justify-center',
          compact ? 'px-4' : 'px-1',
        )}
        size={compact ? 'small' : 'medium'}
      />
    );
  }

  return (
    <>
      {icon ? (
        <LinkButtonIcon
          compact={compact}
          icon={icon}
          iconProps={iconProps}
          rightAligned={rightAligned}
          subtle={subtle}
        />
      ) : null}
      <span className="whitespace-nowrap break-all">{label || children}</span>
    </>
  );
}

export function LinkButton(props: LinkButtonProps) {
  const isKunde = useTheme() === 'kunde';
  const minLg = useBreakpointMinLg();
  const {
    label,
    compact = isKunde ? minLg : undefined, // default compact styling for sizes above tablet
    inverted,
    rightAligned,
    icon,
    iconProps,
    loading,
    to,
    href,
    onClick,
    className,
    subtle,
    theme,
    children,
    ...rest
  } = props;

  const classes = getLinkButtonClasses({
    className,
    compact,
    icon,
    inverted,
    label,
    loading,
    rightAligned,
    subtle,
    theme,
  });
  const buttonContent = (
    <LinkButtonContent
      compact={compact}
      icon={icon}
      iconProps={iconProps}
      label={label}
      loading={loading}
      rightAligned={rightAligned}
      subtle={subtle}
    >
      {children}
    </LinkButtonContent>
  );

  if (href) {
    return (
      <a
        href={href}
        className={cn(classes, { 'pointer-events-none': rest.disabled })}
        rel="nofollow"
        {...rest}
        tabIndex={rest.disabled ? -1 : undefined}
        aria-disabled={rest.disabled ? 'true' : undefined}
      >
        {buttonContent}
      </a>
    );
  }

  if (to) {
    return (
      <Link
        to={to}
        className={cn(classes, { 'pointer-events-none': rest.disabled })}
        {...rest}
        tabIndex={rest.disabled ? -1 : undefined}
        aria-disabled={rest.disabled ? 'true' : undefined}
      >
        {buttonContent}
      </Link>
    );
  }

  return (
    <button onClick={onClick} className={classes} {...rest}>
      {buttonContent}
    </button>
  );
}

export default LinkButton;
